import { Globe, Mail, MapPin, Phone, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CONTACT } from '../data/contact'

/**
 * One row of the contact card: a circular navy-tint icon well, a muted label,
 * and the value. `href` turns the value into a link; the phone number stays
 * plain text while it is still a placeholder (see data/contact.ts).
 */
function ContactRow({
  icon: Icon,
  label,
  href,
  children,
}: {
  icon: LucideIcon
  label: string
  href?: string | null
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-4">
      <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-full">
        <Icon className="size-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <h3 className="text-label-md text-on-surface-variant mb-1">{label}</h3>
        {href ? (
          <a
            href={href}
            className="text-body-md text-primary hover:text-secondary rounded-sm font-medium break-words hover:underline"
          >
            {children}
          </a>
        ) : (
          <p className="text-body-md text-primary font-medium break-words">{children}</p>
        )}
      </div>
    </div>
  )
}

export function ContactSection() {
  const { t } = useTranslation()

  return (
    <section id="contact" className="border-outline-variant/30 bg-surface py-section border-t">
      <div className="max-w-content px-margin-mobile md:px-margin-tablet lg:px-margin-desktop mx-auto">
        <div className="gap-stack-lg lg:gap-gutter grid lg:grid-cols-2">
          <div className="flex flex-col">
            <h2 className="font-display text-headline-lg text-primary">{t('contact.heading')}</h2>
            <p className="mt-stack-sm text-body-md text-on-surface-variant max-w-md">
              {t('contact.intro')}
            </p>

            <div className="mt-stack-md divide-outline-variant/40 border-outline-variant bg-surface-container-lowest shadow-e1 divide-y rounded-lg border">
              <div className="p-stack-md">
                <ContactRow icon={MapPin} label={t('contact.addressLabel')}>
                  {t('contact.addressLine1')}
                  <br />
                  {t('contact.addressLine2')}
                </ContactRow>
              </div>
              <div className="p-stack-md">
                <ContactRow icon={Mail} label={t('contact.emailLabel')} href={CONTACT.emailHref}>
                  {CONTACT.email}
                </ContactRow>
              </div>
              <div className="p-stack-md">
                <ContactRow icon={Phone} label={t('contact.phoneLabel')} href={CONTACT.phoneHref}>
                  {CONTACT.phone}
                </ContactRow>
              </div>
              <div className="p-stack-md">
                <ContactRow
                  icon={Globe}
                  label={t('contact.websiteLabel')}
                  href={CONTACT.websiteHref}
                >
                  {CONTACT.website}
                </ContactRow>
              </div>
            </div>
          </div>

          <div className="border-outline-variant bg-surface-container-low shadow-e1 min-h-[400px] overflow-hidden rounded-lg border">
            <iframe
              title={t('contact.mapTitle')}
              src={CONTACT.mapEmbedUrl}
              className="size-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  )
}
